import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Save, Loader2, AlertCircle, CheckCircle, HelpCircle,
  Eye, EyeOff, RefreshCw, Send, ExternalLink, Copy, Check,
  Zap, Shield, Globe, Webhook, Database, BarChart3, FlaskConical, XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { api } from '../../lib/auth';
import { toast } from 'sonner';

// Tooltip component for helper text
function Tooltip({ children, text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <span 
        onMouseEnter={() => setShow(true)} 
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </span>
      {show && (
        <span className="absolute left-full ml-2 z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap">
          {text}
        </span>
      )}
    </span>
  );
}

// Section Header Component
function SectionHeader({ icon: Icon, title, description, color }) {
  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg bg-gradient-to-r ${color} mb-4`}>
      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h3 className="font-semibold text-white">{title}</h3>
        <p className="text-sm text-white/70">{description}</p>
      </div>
    </div>
  );
}

// Field with helper text
function SettingField({ label, helper, required, children }) {
  return (
    <div className="space-y-1">
      <label className="text-sm text-gray-300 flex items-center gap-2">
        {label}
        {required && <span className="text-red-400 text-xs">*</span>}
        {helper && (
          <Tooltip text={helper}>
            <HelpCircle className="w-3.5 h-3.5 text-gray-500 hover:text-gray-300" />
          </Tooltip>
        )}
      </label>
      {children}
    </div>
  );
}

export function TrackingRetargetingSettings({ businessId }) {
  const [settings, setSettings] = useState({
    // Meta (Facebook/Instagram)
    meta_pixel_id: '',
    meta_access_token: '',
    meta_test_event_code: '',
    
    // Google
    gtm_container_id: '',
    google_ads_conversion_id: '',
    ga4_measurement_id: '',
    
    // Catalog / Feed Connection
    meta_catalog_id: '',
    inventory_feed_enabled: false,
    inventory_feed_type: '',
    inventory_feed_refresh_frequency: 'daily',
    
    // Consent / Compliance
    cookie_consent_enabled: true,
    consent_mode: 'basic',
    tracking_allowed_regions: [],
    
    // Webhook / Audience Orchestration
    audience_webhook_enabled: false,
    audience_webhook_url: '',
    audience_webhook_secret: '',
    audience_webhook_event_types: ['view_item', 'search', 'lead'],
    
    // Overrides
    manual_latitude: '',
    manual_longitude: '',
    
    // Schema version
    schema_version: 'v2_tracking_ready'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingFeed, setTestingFeed] = useState(null);
  const [feedTestResult, setFeedTestResult] = useState(null);
  const [showTokens, setShowTokens] = useState({});
  const [copied, setCopied] = useState(null);
  const [feedUrls, setFeedUrls] = useState({});
  
  useEffect(() => {
    loadSettings();
  }, [businessId]);
  
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/business/tracking-settings');
      if (res.data?.settings) {
        setSettings(prev => ({ ...prev, ...res.data.settings }));
      }
      if (res.data?.feed_urls) {
        setFeedUrls(res.data.feed_urls);
      }
    } catch (error) {
      console.error('Failed to load tracking settings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/business/tracking-settings', settings);
      toast.success('Tracking settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const testWebhook = async () => {
    if (!settings.audience_webhook_url) {
      toast.error('Please enter a webhook URL first');
      return;
    }
    setTesting(true);
    try {
      await api.post('/api/business/tracking-settings/test-webhook', {
        webhook_url: settings.audience_webhook_url,
        webhook_secret: settings.audience_webhook_secret
      });
      toast.success('Test webhook sent successfully');
    } catch (error) {
      toast.error('Webhook test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTesting(false);
    }
  };
  
  const testFeed = async (feedUrl, feedType) => {
    setTestingFeed(feedType);
    setFeedTestResult(null);
    try {
      const res = await api.post('/api/business/tracking-settings/validate-feed', {
        feed_url: feedUrl,
        feed_type: feedType
      });
      setFeedTestResult({
        type: feedType,
        success: res.data.valid,
        details: res.data
      });
      if (res.data.valid) {
        toast.success(`Feed validation passed! ${res.data.item_count} items found.`);
      } else {
        toast.error(`Feed validation failed: ${res.data.errors?.join(', ') || 'Unknown error'}`);
      }
    } catch (error) {
      setFeedTestResult({
        type: feedType,
        success: false,
        details: { errors: [error.response?.data?.detail || error.message] }
      });
      toast.error('Feed test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingFeed(null);
    }
  };
  
  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };
  
  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };
  
  const toggleArrayItem = (field, item) => {
    setSettings(prev => {
      const arr = prev[field] || [];
      if (arr.includes(item)) {
        return { ...prev, [field]: arr.filter(i => i !== item) };
      }
      return { ...prev, [field]: [...arr, item] };
    });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }
  
  const eventTypes = ['view_item', 'search', 'lead', 'segment_assigned', 'purchase', 'add_to_cart'];
  const inventoryTypes = [
    { value: 'vehicles', label: 'Vehicles' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'rvs', label: 'RVs' },
    { value: 'furniture', label: 'Furniture' }
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Tracking & Retargeting Settings</h2>
            <p className="text-sm text-gray-400">Configure pixels, feeds, and audience orchestration for the Directory</p>
          </div>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-500">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
      
      {/* Info Banner */}
      <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
        <p className="text-sm text-blue-300">
          <strong>💡 How it works:</strong> These settings are synced to the AI Business Finder Directory. 
          The Directory will use your pixel IDs to fire ViewContent, Search, and Lead events when visitors interact with your listings.
        </p>
      </div>

      {/* Meta (Facebook/Instagram) Section */}
      <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6">
        <SectionHeader 
          icon={Eye}
          title="Meta (Facebook/Instagram)"
          description="Connect your Meta Pixel for dynamic retargeting on Facebook & Instagram"
          color="from-blue-600/50 to-indigo-600/50"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingField 
            label="Meta Pixel ID" 
            helper="Required for Meta retargeting. Find in Events Manager > Data Sources"
            required
          >
            <Input
              value={settings.meta_pixel_id}
              onChange={(e) => updateField('meta_pixel_id', e.target.value)}
              placeholder="e.g., 1234567890123456"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
          
          <SettingField 
            label="Meta Access Token"
            helper="Optional. For server-side Conversions API. Generate in Events Manager"
          >
            <div className="relative">
              <Input
                type={showTokens.meta_access_token ? 'text' : 'password'}
                value={settings.meta_access_token}
                onChange={(e) => updateField('meta_access_token', e.target.value)}
                placeholder="Your access token"
                className="bg-[#0a0f1a] border-white/10 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowTokens(prev => ({ ...prev, meta_access_token: !prev.meta_access_token }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showTokens.meta_access_token ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </SettingField>
          
          <SettingField 
            label="Test Event Code"
            helper="Optional. Use for testing in Events Manager Test Events tab"
          >
            <Input
              value={settings.meta_test_event_code}
              onChange={(e) => updateField('meta_test_event_code', e.target.value)}
              placeholder="e.g., TEST12345"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
          
          <SettingField 
            label="Meta Catalog ID"
            helper="Recommended. Connect a Catalog for dynamic vehicle/property ads"
          >
            <Input
              value={settings.meta_catalog_id}
              onChange={(e) => updateField('meta_catalog_id', e.target.value)}
              placeholder="e.g., 1234567890123456"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
        </div>
      </div>

      {/* Google Section */}
      <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6">
        <SectionHeader 
          icon={BarChart3}
          title="Google Tracking"
          description="Connect Google Tag Manager, Analytics, and Ads for cross-platform retargeting"
          color="from-yellow-600/50 to-orange-600/50"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SettingField 
            label="GTM Container ID"
            helper="Preferred. Google Tag Manager container for centralized tag management"
          >
            <Input
              value={settings.gtm_container_id}
              onChange={(e) => updateField('gtm_container_id', e.target.value)}
              placeholder="e.g., GTM-XXXXXXX"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
          
          <SettingField 
            label="Google Ads Conversion ID"
            helper="Optional. For conversion tracking and remarketing"
          >
            <Input
              value={settings.google_ads_conversion_id}
              onChange={(e) => updateField('google_ads_conversion_id', e.target.value)}
              placeholder="e.g., AW-1234567890"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
          
          <SettingField 
            label="GA4 Measurement ID"
            helper="Optional. For Google Analytics 4 event tracking"
          >
            <Input
              value={settings.ga4_measurement_id}
              onChange={(e) => updateField('ga4_measurement_id', e.target.value)}
              placeholder="e.g., G-XXXXXXXXXX"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
        </div>
      </div>

      {/* Catalog / Feed Connection Section */}
      <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6">
        <SectionHeader 
          icon={Database}
          title="Inventory Feed Connection"
          description="Enable dynamic ads by connecting your inventory feed to ad platforms"
          color="from-emerald-600/50 to-teal-600/50"
        />
        
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.inventory_feed_enabled}
                onChange={(e) => updateField('inventory_feed_enabled', e.target.checked)}
                className="rounded border-gray-500 bg-[#0a0f1a]"
              />
              <span className="text-white">Enable Inventory Feed</span>
            </label>
          </div>
          
          {settings.inventory_feed_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <SettingField 
                label="Feed Type"
                helper="Select your primary inventory type"
                required
              >
                <select
                  value={settings.inventory_feed_type}
                  onChange={(e) => updateField('inventory_feed_type', e.target.value)}
                  className="w-full bg-[#0a0f1a] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                  style={{ backgroundColor: '#0a0f1a' }}
                >
                  <option value="">Select feed type</option>
                  {inventoryTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </SettingField>
              
              <SettingField 
                label="Refresh Frequency"
                helper="How often the feed should be refreshed"
              >
                <select
                  value={settings.inventory_feed_refresh_frequency}
                  onChange={(e) => updateField('inventory_feed_refresh_frequency', e.target.value)}
                  className="w-full bg-[#0a0f1a] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                  style={{ backgroundColor: '#0a0f1a' }}
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily (Recommended)</option>
                  <option value="weekly">Weekly</option>
                </select>
              </SettingField>
            </div>
          )}
          
          {/* Feed URLs */}
          {Object.keys(feedUrls).length > 0 && (
            <div className="mt-4 p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-white">Your Feed URLs</h4>
                {Object.entries(feedUrls).filter(([key]) => key.endsWith('_xml')).length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const xmlFeed = Object.entries(feedUrls).find(([key]) => key.endsWith('_xml'));
                      if (xmlFeed) testFeed(xmlFeed[1], xmlFeed[0]);
                    }}
                    disabled={testingFeed !== null}
                    className="text-xs"
                  >
                    {testingFeed ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <FlaskConical className="w-3 h-3 mr-1" />
                    )}
                    Test XML Feed
                  </Button>
                )}
              </div>
              
              {/* Feed Test Result */}
              {feedTestResult && (
                <div className={`mb-3 p-3 rounded-lg ${feedTestResult.success ? 'bg-emerald-900/30 border border-emerald-500/30' : 'bg-red-900/30 border border-red-500/30'}`}>
                  <div className="flex items-start gap-2">
                    {feedTestResult.success ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${feedTestResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                        {feedTestResult.success ? 'Feed Validation Passed!' : 'Feed Validation Failed'}
                      </p>
                      {feedTestResult.details && (
                        <div className="text-xs text-gray-400 mt-1 space-y-1">
                          {feedTestResult.details.item_count !== undefined && (
                            <p>Items found: <span className="text-white">{feedTestResult.details.item_count}</span></p>
                          )}
                          {feedTestResult.details.schema_valid !== undefined && (
                            <p>Schema valid: <span className={feedTestResult.details.schema_valid ? 'text-emerald-400' : 'text-red-400'}>{feedTestResult.details.schema_valid ? 'Yes' : 'No'}</span></p>
                          )}
                          {feedTestResult.details.required_fields_present !== undefined && (
                            <p>Required fields: <span className={feedTestResult.details.required_fields_present ? 'text-emerald-400' : 'text-amber-400'}>{feedTestResult.details.required_fields_present ? 'All present' : 'Some missing'}</span></p>
                          )}
                          {feedTestResult.details.warnings?.length > 0 && (
                            <div className="mt-1">
                              <p className="text-amber-400">Warnings:</p>
                              <ul className="list-disc list-inside text-amber-300/80">
                                {feedTestResult.details.warnings.map((w, i) => <li key={i}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                          {feedTestResult.details.errors?.length > 0 && (
                            <div className="mt-1">
                              <p className="text-red-400">Errors:</p>
                              <ul className="list-disc list-inside text-red-300/80">
                                {feedTestResult.details.errors.map((e, i) => <li key={i}>{e}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => setFeedTestResult(null)}
                      className="text-gray-500 hover:text-white"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                {/* Group XML and JSON feeds separately */}
                {Object.entries(feedUrls).filter(([key]) => key.endsWith('_xml')).length > 0 && (
                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Meta Catalog (XML)</span>
                    {Object.entries(feedUrls)
                      .filter(([key]) => key.endsWith('_xml'))
                      .map(([type, url]) => (
                        <div key={type} className="flex items-center gap-2">
                          <span className="text-sm text-gray-400 capitalize w-28">{type.replace('_xml', '').replace('_', ' ')}:</span>
                          <code className="flex-1 text-xs text-emerald-400 bg-black/30 px-2 py-1 rounded truncate">
                            {url}
                          </code>
                          <button
                            onClick={() => copyToClipboard(url, type)}
                            className="text-gray-400 hover:text-white flex-shrink-0"
                            title="Copy URL"
                          >
                            {copied === type ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => testFeed(url, type)}
                            disabled={testingFeed === type}
                            className="text-gray-400 hover:text-cyan-400 flex-shrink-0"
                            title="Validate Feed"
                          >
                            {testingFeed === type ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                          </button>
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white flex-shrink-0"
                            title="Open in new tab"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                    ))}
                  </div>
                )}
                
                {Object.entries(feedUrls).filter(([key]) => key.endsWith('_json')).length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Internal/API (JSON)</span>
                    {Object.entries(feedUrls)
                      .filter(([key]) => key.endsWith('_json'))
                      .map(([type, url]) => (
                        <div key={type} className="flex items-center gap-2">
                          <span className="text-sm text-gray-400 capitalize w-28">{type.replace('_json', '').replace('_', ' ')}:</span>
                          <code className="flex-1 text-xs text-purple-400 bg-black/30 px-2 py-1 rounded truncate">
                            {url}
                          </code>
                          <button
                            onClick={() => copyToClipboard(url, type)}
                            className="text-gray-400 hover:text-white flex-shrink-0"
                          >
                            {copied === type ? <Check className="w-4 h-4 text-purple-400" /> : <Copy className="w-4 h-4" />}
                          </button>
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white flex-shrink-0"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                <strong>XML feeds</strong> are for Meta Catalog/Dynamic Ads. <strong>JSON feeds</strong> are for internal use and the AI Business Finder Directory.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Consent / Compliance Section */}
      <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6">
        <SectionHeader 
          icon={Shield}
          title="Consent & Compliance"
          description="Configure cookie consent and tracking compliance settings"
          color="from-purple-600/50 to-pink-600/50"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.cookie_consent_enabled}
                onChange={(e) => updateField('cookie_consent_enabled', e.target.checked)}
                className="rounded border-gray-500 bg-[#0a0f1a]"
              />
              <span className="text-white">Cookie Consent Enabled</span>
            </label>
            <Tooltip text="When enabled, tracking pixels will only fire after consent is granted">
              <HelpCircle className="w-4 h-4 text-gray-500" />
            </Tooltip>
          </div>
          
          <SettingField 
            label="Consent Mode"
            helper="Basic: simple on/off. Advanced: Google Consent Mode v2"
          >
            <select
              value={settings.consent_mode}
              onChange={(e) => updateField('consent_mode', e.target.value)}
              className="w-full bg-[#0a0f1a] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
              style={{ backgroundColor: '#0a0f1a' }}
            >
              <option value="basic">Basic</option>
              <option value="advanced">Advanced (Google Consent Mode v2)</option>
            </select>
          </SettingField>
        </div>
      </div>

      {/* Webhook / Audience Orchestration Section */}
      <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6">
        <SectionHeader 
          icon={Webhook}
          title="Webhook & Audience Orchestration"
          description="Send real-time events to TV, Radio, Direct Mail, DSPs, CRM, and Ad Ops systems"
          color="from-cyan-600/50 to-blue-600/50"
        />
        
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.audience_webhook_enabled}
                onChange={(e) => updateField('audience_webhook_enabled', e.target.checked)}
                className="rounded border-gray-500 bg-[#0a0f1a]"
              />
              <span className="text-white">Enable Audience Webhook</span>
            </label>
          </div>
          
          {settings.audience_webhook_enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <SettingField 
                  label="Webhook URL"
                  helper="URL to receive event payloads"
                  required
                >
                  <Input
                    value={settings.audience_webhook_url}
                    onChange={(e) => updateField('audience_webhook_url', e.target.value)}
                    placeholder="https://your-system.com/webhook"
                    className="bg-[#0a0f1a] border-white/10 text-white"
                  />
                </SettingField>
                
                <SettingField 
                  label="Webhook Secret"
                  helper="For HMAC signature verification"
                >
                  <div className="relative">
                    <Input
                      type={showTokens.webhook_secret ? 'text' : 'password'}
                      value={settings.audience_webhook_secret}
                      onChange={(e) => updateField('audience_webhook_secret', e.target.value)}
                      placeholder="Your webhook secret"
                      className="bg-[#0a0f1a] border-white/10 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowTokens(prev => ({ ...prev, webhook_secret: !prev.webhook_secret }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showTokens.webhook_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </SettingField>
              </div>
              
              <SettingField 
                label="Event Types to Send"
                helper="Select which events trigger webhook calls"
              >
                <div className="flex flex-wrap gap-2">
                  {eventTypes.map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleArrayItem('audience_webhook_event_types', type)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        (settings.audience_webhook_event_types || []).includes(type)
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {type.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </SettingField>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={testWebhook}
                  disabled={testing || !settings.audience_webhook_url}
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test Ping
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Location Overrides Section */}
      <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6">
        <SectionHeader 
          icon={Globe}
          title="Location Overrides"
          description="Optional manual coordinates (will not overwrite canonical NAP address)"
          color="from-gray-600/50 to-slate-600/50"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingField 
            label="Manual Latitude"
            helper="Optional. Override geocoded latitude"
          >
            <Input
              type="number"
              step="0.000001"
              value={settings.manual_latitude}
              onChange={(e) => updateField('manual_latitude', e.target.value)}
              placeholder="e.g., 25.7617"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
          
          <SettingField 
            label="Manual Longitude"
            helper="Optional. Override geocoded longitude"
          >
            <Input
              type="number"
              step="0.000001"
              value={settings.manual_longitude}
              onChange={(e) => updateField('manual_longitude', e.target.value)}
              placeholder="e.g., -80.1918"
              className="bg-[#0a0f1a] border-white/10 text-white"
            />
          </SettingField>
        </div>
      </div>

      {/* Validation Summary */}
      <div className="bg-[#0d1525] rounded-xl border border-white/10 p-6">
        <h3 className="font-semibold text-white mb-4">Configuration Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            {settings.meta_pixel_id ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-400" />
            )}
            <span className="text-sm text-gray-300">Meta Pixel</span>
          </div>
          <div className="flex items-center gap-2">
            {settings.gtm_container_id || settings.ga4_measurement_id ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-400" />
            )}
            <span className="text-sm text-gray-300">Google Tags</span>
          </div>
          <div className="flex items-center gap-2">
            {settings.inventory_feed_enabled && settings.inventory_feed_type ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-gray-500" />
            )}
            <span className="text-sm text-gray-300">Feed Enabled</span>
          </div>
          <div className="flex items-center gap-2">
            {settings.cookie_consent_enabled ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-400" />
            )}
            <span className="text-sm text-gray-300">Consent</span>
          </div>
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
        <h4 className="font-medium text-amber-400 mb-2">To enable hyper-targeted ads:</h4>
        <ul className="text-sm text-amber-300/80 space-y-1 list-disc list-inside">
          <li>Add your Meta Pixel ID (and optionally Conversions API token)</li>
          <li>Add your Google Tag Manager ID or GA4 ID</li>
          <li>Keep inventory uploaded with VIN/listing IDs and at least 1 photo</li>
          <li>(Recommended) Connect your Meta Catalog using the feed link we provide</li>
          <li>Configure consent settings</li>
        </ul>
      </div>
    </div>
  );
}

export default TrackingRetargetingSettings;
